import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { uuidv7, type CommunityDetail, type Conversation, type DirectMessage, type FriendItem } from "@atrium/shared";
import { api, errorMessage } from "../../lib/api";
import { Dialog } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { Avatar } from "../../components/ui/Avatar";
import { Spinner } from "../../components/ui/Spinner";
import { toast } from "../../components/ui/Toast";
import { IconCheck, IconCopy, IconSearch } from "../../components/icons";
import { copyText } from "../posts/usePostActions";
import { useFriends } from "../relationships/hooks";
import { encryptForConversation, rememberPlaintext, withFreshKey } from "../messages/e2eeMessages";

export function InviteDialog({ open, onOpenChange, community }: { open: boolean; onOpenChange: (o: boolean) => void; community: CommunityDetail }) {
  const [link, setLink] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [q, setQ] = useState("");
  const [sent, setSent] = useState<Set<string>>(new Set());
  const [sending, setSending] = useState<Set<string>>(new Set());
  const qc = useQueryClient();
  const friends = useFriends("all", open);
  const needle = q.trim().toLowerCase();
  const shown = (friends.data ?? []).filter((f) => f.status === "friends").filter((f) => !needle || f.displayName.toLowerCase().includes(needle) || f.username.toLowerCase().includes(needle));

  useEffect(() => {
    if (!open) {
      setLink(null);
      setQ("");
      setSent(new Set());
      setSending(new Set());
      setCopied(false);
      return;
    }
    setLinkError(null);
    api
      .post<{ code: string }>(`/api/communities/${community.id}/invites`, { maxUses: 0, expiresInHours: 24 * 7 })
      .then((r) => setLink(`${window.location.origin}/invite/${r.code}`))
      .catch((e) => setLinkError(errorMessage(e, "Couldn't create an invite link")));
  }, [open, community.id]);

  /** Sends the invite link in your DM with them; it shows there as an invite card. */
  const sendTo = async (f: FriendItem) => {
    if (!link) return;
    setSending((s) => new Set(s).add(f.id));
    try {
      const c = await api.post<Conversation>("/api/conversations", { userIds: [f.id] });
      const nonce = uuidv7();
      if (c.encryption.enabled) {
        const msg = await withFreshKey(c, async (current) => {
          const { payload } = await encryptForConversation(current, { content: link });
          return api.post<DirectMessage>(`/api/conversations/${c.id}/messages`, { content: "", encrypted: payload, nonce });
        });
        rememberPlaintext(msg.id, link);
      } else {
        await api.post(`/api/conversations/${c.id}/messages`, { content: link, mediaIds: [], nonce });
      }
      void qc.invalidateQueries({ queryKey: ["conversations"] });
      setSent((s) => new Set(s).add(f.id));
    } catch (e) {
      toast.error(errorMessage(e, `Couldn't send the invite to ${f.displayName}`));
    } finally {
      setSending((s) => {
        const next = new Set(s);
        next.delete(f.id);
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title={`Invite to ${community.name}`}>
      <div className="space-y-5">
        <div className="space-y-1.5">
          <div className="text-sm font-medium text-fg-2">Invite link</div>
          <div className="flex gap-2">
            <Input readOnly value={link ?? (linkError ? "" : "Creating link…")} aria-label="Invite link" className="font-mono text-sm" onFocus={(e) => e.target.select()} />
            <Button
              variant="primary"
              icon={copied ? IconCheck : IconCopy}
              disabled={!link}
              onClick={() => {
                if (!link) return;
                void copyText(link);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          {linkError ? <p className="text-xs text-danger">{linkError}</p> : <p className="text-xs text-fg-3">Expires in 7 days.</p>}
        </div>
        <div className="space-y-1.5">
          <div className="text-sm font-medium text-fg-2">Send to friends</div>
          <div className="relative">
            <IconSearch className="icon pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-fg-3" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search friends" className="pl-9" aria-label="Search friends" />
          </div>
          <div className="max-h-72 min-h-24 overflow-y-auto">
            {friends.isPending ? (
              <div className="flex justify-center py-6 text-fg-3">
                <Spinner className="size-4" />
              </div>
            ) : shown.length === 0 ? (
              <p className="px-2 py-4 text-sm text-fg-3">{needle ? "No friends match that." : "Add friends to send them invites in a message."}</p>
            ) : (
              shown.map((f) => (
                <div key={f.id} className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-surface">
                  <Avatar src={f.avatarUrl} name={f.displayName} size="md" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{f.displayName}</div>
                    <div className="truncate text-xs text-fg-3">@{f.username}</div>
                  </div>
                  <Button
                    size="sm"
                    variant={sent.has(f.id) ? "ghost" : "secondary"}
                    icon={sent.has(f.id) ? IconCheck : undefined}
                    disabled={!link || sent.has(f.id)}
                    loading={sending.has(f.id)}
                    onClick={() => void sendTo(f)}
                  >
                    {sent.has(f.id) ? "Sent" : "Send"}
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
